import React from 'react';

import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

import styled from '@emotion/styled';
import { useField } from 'formik';

import { SHIPPING_AVAILABILITY } from '../../../components/Dialogs/CollabsFilters/options';
import { HtmlTooltip } from '../../../components/HtmlTooltip/HtmlTooltip';
import { BlackIOSSwitch } from '../../../components/IOSSwitch';
import { ShippingRegion } from '../../../graphql/API';
import Icons from '../../../themes/icons';
import { StepOneFormValue, StepThreeFormValue, StepTwoFormValue } from './schema';

const individualRegions = Object.values(ShippingRegion).filter(
  (r) => r !== ShippingRegion.global,
);

const SUGGESTED_REGIONS: ShippingRegion[] = [ShippingRegion.us, ShippingRegion.eu];

type Values = StepOneFormValue & StepTwoFormValue & StepThreeFormValue;

type Props = {
  label?: string;
  name?: string;
  defaultValue?: string;
  values: Values;
};

const ShippingAvailability: React.FC<Props> = ({
  label = 'Shipping availability',
  name = 'shippingRegions',
  defaultValue = 'global',
  values,
}) => {
  const [, meta, helper] = useField(name);

  const currentRegions = values.shippingRegions || [];
  const isGlobalStored = currentRegions.includes(ShippingRegion.global);
  const allIndividualsSelected =
    !isGlobalStored && individualRegions.every((r) => currentRegions.includes(r));
  const isGlobal = isGlobalStored || allIndividualsSelected;

  const isRegionChecked = (region: ShippingRegion) =>
    isGlobal || currentRegions.includes(region);

  const handleRegionChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, region: ShippingRegion) => {
      const checked = e.target.checked;
      const current = values.shippingRegions || [];
      const isCurrentlyGlobal = current.includes(ShippingRegion.global);

      if (checked) {
        const baseline = isCurrentlyGlobal
          ? [...individualRegions]
          : current.filter((r) => r !== ShippingRegion.global);
        const next = baseline.includes(region) ? baseline : [...baseline, region];
        const allSelected = individualRegions.every((r) => next.includes(r));
        helper.setValue(allSelected ? [ShippingRegion.global] : next);
      } else {
        const baseline = isCurrentlyGlobal
          ? individualRegions
          : current.filter((r) => r !== ShippingRegion.global);
        helper.setValue(baseline.filter((r) => r !== region));
      }
    },
    [helper, values.shippingRegions],
  );

  return (
    <Box>
      <Box display={'flex'} alignItems={'center'} gap={'8px'} mb={'15px'}>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <HtmlTooltip
          arrow
          placement='top'
          title={
            <React.Fragment>
              <Typography fontSize={'15px'} p={'12px'}>
                The region where you agree to ship your product
              </Typography>
            </React.Fragment>
          }
        >
          <Icons.InfoIcon />
        </HtmlTooltip>
      </Box>

      <FormControlLabel
        sx={{ m: 0, gap: '6px' }}
        control={
          <BlackIOSSwitch
            checked={isGlobal}
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                helper.setValue([ShippingRegion.global]);
              } else {
                helper.setValue([ShippingRegion.us]);
              }
            }}
          />
        }
        label={SHIPPING_AVAILABILITY['global']}
        labelPlacement='top'
      />

      <FormGroup sx={{ my: 1 }}>
        {individualRegions.map((value) => (
          <FormControlLabel
            key={value}
            control={
              <Checkbox
                checked={isRegionChecked(value)}
                onChange={(e) => handleRegionChange(e, value)}
              />
            }
            label={
              <Box display='flex' alignItems='center' gap='8px'>
                <span>{SHIPPING_AVAILABILITY[value]}</span>
                {SUGGESTED_REGIONS.includes(value) && <SuggestedTag>Suggested</SuggestedTag>}
              </Box>
            }
            sx={{ '& .MuiSvgIcon-root': { fontSize: 26 } }}
          />
        ))}
      </FormGroup>
      {meta.error && meta.touched && <FormError>{meta.error}</FormError>}
    </Box>
  );
};

export default ShippingAvailability;

const FormError = styled.span`
  margin: 1rem 0;
  color: #ff3d3d;
`;

const SuggestedTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: #e6f6ec;
  color: #1f8a4c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.2px;
`;
